import { FC, useEffect } from "react";
import TimeSelectors from "./TimeSelectors";
import { Text } from "rizzui";
import CustomSelect from "../../../components/forms/CustomSelect";
import { useFormik } from "formik";
import moment from 'moment';
import { AuthState } from "../../../redux/authSlice";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import RequiredInput from "../../../components/forms/InputHOC";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import { useCensusFormContext } from "./provider/CensusFormProvider";
import { generalDataValidationSchema, SuperAdminSchema } from "./types/CensusSchemas";

interface CensusGeneralDataFormProps {
    formik: any;
    onValidationChange?: (isValid: boolean) => void;
    isEdit?: boolean;
    parkId: string | null;
}

const CensusGeneralDataForm: FC<CensusGeneralDataFormProps> = ({ formik, onValidationChange, isEdit = false, parkId = null }) => {

    // PROVIDER

    const { parksList, usersList, companyList, statusList } = useCensusFormContext();

    // HOOKS

    const { user }: AuthState = useSelector((state: RootState) => state.auth);
    const { userCan } = usePrivilege();
        
    const isAdmin = user?.roles?.includes('Coordinador') || user?.roles?.includes('Administrador') || user?.roles?.includes('Superadministrador') ? true : false;

    // FORMIK 

    // IF DON'T HAVE FORMIK, USE INTERNAL FORMIK
    
    const internalFormik = useFormik({
        initialValues: {
            parkId: parkId ? parkId : '',
            company: '',
            userId: !isAdmin ? user?.id : '',
            date: '',
            time: '',
            endTime: '',
            censusDpma: '',
            observationPost: '',
            year: '',
            month: '',
            week: '',
        },
        validationSchema: userCan('list', 'companies') ? SuperAdminSchema : generalDataValidationSchema,
        validateOnChange: true,
        onSubmit: () => {},
    });

    const activeFormik = formik || internalFormik;

    // USE EFFECT

    useEffect(() => {
        if (onValidationChange) {
            activeFormik.validateForm().then((errors: any) => {
                const isValid = Object.keys(errors).length === 0;
                onValidationChange(isValid);
            });
        }
    }, []);

    useEffect(() => {
        if (formik.values.date) {
            // La fecha viene en formato DD/MM/YYYY desde TimeSelectors
            const selectedDate = moment(formik.values.date, 'DD/MM/YYYY');
            if (selectedDate.isValid()) {
                const year = selectedDate.format('YYYY');
                const month = selectedDate.month() + 1; // moment months are 0-indexed (0=enero, 11=diciembre)
                const week = selectedDate.week().toString(); 
                
                // Solo actualizar si los valores son diferentes para evitar loops infinitos
                if (formik.values.year !== year) {
                    formik.setFieldValue('year', year);
                }
                if (formik.values.month !== month) {
                    formik.setFieldValue('month', month);
                }
                if (formik.values.week !== week) {
                    formik.setFieldValue('week', week);
                }
            }
        }
    }, [formik.values.date]);

    // RENDER

    return (
        <div className="space-y-6">
            <Text className="text-lg font-medium">Datos Generales del Censo</Text>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                {/* PARK SELECTOR */}

                <CustomSelect
                    isSearchable
                    id={'park'}
                    label="Parque Eólico"
                    isDisabled={parkId ? true : false}
                    options={parksList}
                    onChange={(e: any) => { 
                        formik.setFieldValue('parkId', e.value)
                    }}
                    value={
                        parksList.filter((park: any) => park.value === formik.values.parkId)
                    }
                    required
                    error={formik.touched.parkId && formik.errors.parkId ?  formik.errors.parkId : null}
                />

                {/* USER SELECTOR */}

                <CustomSelect
                    isSearchable
                    id={'userId'}
                    label="Observador"
                    options={usersList}
                    onChange={(e: any) => {
                        formik.setFieldValue('userId', e.value)
                    }}
                    value={
                        isAdmin ? usersList.filter((user: any) => user.value === formik.values.userId) : user ? [{
                            value: user.id,
                            label: user.name + ' ' + (user.lastName ? `${user.lastName}` : '')
                        }] : []
                    }
                    required
                    error={formik.touched.userId && formik.errors.userId ?  formik.errors.userId : null}
                    isDisabled={!isAdmin && user?.id ? true : false}
                />
                
                <TimeSelectors formik={activeFormik} className="md:col-span-2" required />

                <RequiredInput
                    id={'censusDpma'}
                    label="Censo DPMA"
                    value={formik.values.censusDpma}
                    onChange={(e) => formik.setFieldValue('censusDpma', e.target.value)}
                    required
                    error={formik.touched.censusDpma && formik.errors.censusDpma ?  formik.errors.censusDpma : null}
                />

                <RequiredInput
                    id={'observationPost'}
                    label="Puesto de observación"
                    value={formik.values.observationPost}
                    onChange={(e) => formik.setFieldValue('observationPost', e.target.value)}
                    required
                    error={formik.touched.observationPost && formik.errors.observationPost ?  formik.errors.observationPost : null}
                />

                {
                    userCan('list', 'companies') && (
                        <div className="md:col-span-2">
                            <CustomSelect
                                isSearchable
                                id={'company'}
                                label="Compañia"
                                options={companyList}
                                onChange={(e: any) => { 
                                    formik.setFieldValue('company', e.value)
                                }}
                                value={
                                    companyList.filter((company: any) => company.value === formik.values.company)
                                }
                                className="mb-4"
                            />
                        </div>
                    )
                }

                {isEdit && (
                    <div className="md:col-span-2">
                        <CustomSelect
                            isSearchable
                            id={'statusId'}
                            label="Estado"
                            options={statusList}
                            onChange={(e: any) => {
                                formik.setFieldValue('statusId', e.value)
                            }}
                            value={
                                statusList.filter((status: any) => status.value === formik.values.statusId)
                            }
                            required
                            className={`mb-4 ${userCan('list', 'companies') ? '' : 'col-span-2'}`}
                        />
                    </div>
                )}
                
            </div>
        </div>
    );
};

export default CensusGeneralDataForm;
